import React, { useEffect, useRef, useState } from "react";
import CommonTable from "components/ReactTable/Table";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Container,
  CardFooter,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import RightPanelCard from "components/Common/RightPanelCard";
import {
  fixedWidthCustomStyle,
  MASTER_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
  customStyle,
  CREATED_BY,
} from "components/Common/constants";
import Select, { components } from "react-select";
import NotificationAlert from "react-notification-alert";
import { showAlert } from "components/Common/commonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { productCategoryList } from "constants/productCategories";
import {
  addProductCategory,
  getProductCategoryList,
  editProductCategory,
} from "actions/productCategories";
import { getManufacturerList } from "actions/manufacturer";
import { manufacturerList } from "constants/manufacturer";

const initialFields = {
  categoryName: {
    value: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    error: "",
  },
  createdBy: {
    value: "",
    error: "",
  },
  manufacturer: {
    value: "",
    error: "",
  },
};

const ProductCategory = () => {
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);

  const productCategoriesList = useSelector(
    (state) => state.productCategory.productCategoryList,
  );

  const manufacturerListData = useSelector(
    (state) => state.manufacturer.manufacturerList,
  );

  const [pageNum, setPageNum] = useState(1);
  const [mode, setMode] = useState({
    mode: "ADD",
    category: null,
    modalOpen: false,
  }); // "add" or "edit" with category id
  const [productCategoryData, setProductCategoryData] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [searchState, setSearchState] = useState({
    query: "",
    status: null,
    manufacturer: null,
    createdBy: null,
    filterParams: [],
  });
  const [productListState, setProductListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  // added the Srno before the list
  const formattedProductCateList =
    productCategoriesList?.data?.productCategory?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
        (productCategoriesList?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  //api to fetch product-categories list
  useEffect(() => {
    getProductCategoryList(dispatch, {
      pageNum: pageNum,
      filters: searchState.filterParams,
    });
    return () => {
      dispatch({ type: productCategoryList, payload: {} });
    }; //unmount the redux data
  }, [dispatch, pageNum, searchState.filterParams]);

  useEffect(() => {
    let filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getManufacturerList(dispatch, { filters: JSON.stringify(filterJSON) });
    return () => dispatch({ type: manufacturerList, payload: {} });
  }, [dispatch]);

  //to check the code and message
  useEffect(() => {
    if (productCategoriesList?.code === 200) {
      setProductListState({
        isLoading: false,
        error: "",
        noRecords: productCategoriesList?.data?.productCategory?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (productCategoriesList?.code || productCategoriesList?.message) {
      setProductListState({
        isLoading: false,
        error:
          productCategoriesList?.message ||
          "Failed to load product category list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [productCategoriesList]);

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState?.query)
      filterParams.push({
        field: "Category",
        condition: "like",
        filterValues: [searchState.query],
      });

    if (searchState?.manufacturer?.ID)
      filterParams.push({
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(searchState.manufacturer.ID)],
      });

    if (searchState?.createdBy?.value)
      filterParams.push({
        field: "CreatedBy",
        condition: "eq",
        filterValues: [searchState.createdBy.value],
      });

    if (searchState?.status?.value)
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });

    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  //add modal
  const openAddModal = () => {
    setMode({ mode: "ADD", category: null, modalOpen: true });
    setProductCategoryData({
      categoryName: { value: "", error: "" },
      status: { value: "ACTIVE", error: "" },
      manufacturer: { value: "", error: "" },
    });
  };

  //edit modal
  const openEditModal = (category) => {
    setMode({ mode: "EDIT", category, modalOpen: true });

    const manufacturerValue = category.manufacturerResponse
      ? {
        name: category.manufacturerResponse.name,
        ID: category.manufacturerResponse.ID,
      }
      : "";

    setProductCategoryData({
      categoryName: { value: category.category || "", error: "" },
      status: { value: category.status || "ACTIVE", error: "" },
      createdBy: { value: category.createdBy || "", error: "" },
      manufacturer: { value: manufacturerValue, error: "" },
    });
  };

  const closeModal = () => {
    setMode((prev) => ({ ...prev, modalOpen: false }));
    setProductCategoryData(initialFields);
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "categoryName":
        return !value.trim() ? "Please enter the category name" : "";
      case "createdBy":
        return mode.mode === "EDIT" && !value ? "Please select created by" : "";
      case "manufacturer":
        return !value ? "Please select a Manufacturer" : "";
      case "status":
        return !value ? "Please select status" : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    let newProductCategory = { ...productCategoryData };
    Object.keys(newProductCategory).forEach((key) => {
      newProductCategory[key].error = getValidationError(
        key,
        newProductCategory[key].value,
      );
    });
    setProductCategoryData(newProductCategory);
    return Object.values(newProductCategory).every((item) => item.error === "");
  };

  const handleInputChange = (field, value) => {
    setProductCategoryData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setProductCategoryData((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: trimmed, error },
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setShowLoaderOnClick(true);
    try {
      const body = {
        category: productCategoryData.categoryName.value,
        manufacturerID: productCategoryData.manufacturer.value?.ID,
        status: productCategoryData.status.value.toUpperCase(),
      };

      const res = await addProductCategory(body);
      if (res.code === 200) {
        notify(
          "success",
          res?.message || "Product category added successfully",
        );
        closeModal();
        pageNum === 1
          ? getProductCategoryList(dispatch, pageNum, searchState.filterParams)
          : setPageNum(1);
      } else notify("danger", res?.message || "Failed to add category.");
    } catch {
      notify("danger", "Failed to add category.");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const handleEdit = async () => {
    if (!validateForm()) return;
    setShowLoaderOnClick(true);
    try {
      const body = {
        category: productCategoryData.categoryName.value,
        createdBy: productCategoryData.createdBy.value,
        manufacturerID: productCategoryData.manufacturer.value?.ID,
        status: productCategoryData.status.value.toUpperCase(),
      };

      const res = await editProductCategory(mode.category.ID, body);
      if (res.code === 200) {
        notify(
          "success",
          res?.message || "Product category updated successfully",
        );
        closeModal();
        getProductCategoryList(dispatch, pageNum, searchState.filterParams);
      } else notify("danger", res?.message || "Failed to update category.");
    } catch {
      notify("danger", "Error updating category");
    } finally {
      setShowLoaderOnClick(false);
    }
  };
  const handleStatusToggle = async (data) => {
    const isActive = data.status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${isActive ? "inactivate" : "activate"
        } this category?`,
      message: `Category "${data.category
        }" will become ${newStatus.toLowerCase()}.`,

      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await editProductCategory(data.ID, {
            category: data.category,
            status: newStatus,
          });
          if (res.code === 200) {
            notify("success", `Status updated to ${newStatus.toLowerCase()}`);
            getProductCategoryList(dispatch, pageNum, searchState.filterParams);
          } else notify("danger", "Failed to update status!");
        } catch {
          notify("danger", "Error updating status");
        }
      },
    });
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setProductListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setSearchState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const clearSearch = () => {
    setProductListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      query: "",
      status: null,
      manufacturer: null,
      createdBy: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const Option = (props) => (
    <components.Option {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );
  const columns = [
  { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
  { Header: "Category Name", accessor: "category", className: "col-pc-category" },
  {
    Header: "Manufacturer Name",
    accessor: "manufacturerResponse",
    className: "col-pc-manufacturer",
    Cell: ({ value }) => value?.name || "",
  },
  {
    Header: "Created By",
    accessor: "createdBy",
    className: "col-pc-createdBy", // added
    Cell: ({ value }) =>
      CREATED_BY.find((item) => item.value === value)?.label || "-",
  },
  {
    Header: "Status",
    accessor: "status",
    className: "col-pc-status", // added
    Cell: ({ value }) => (
      <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
        {value}
      </Badge>
    ),
  },
  {
    Header: "Actions",
    accessor: "ID",
    disableSortBy: true,
    className: "col-pc-actions", // changed from "text-center"
    Cell: ({ row }) => (
      <div className="actions-right d-flex justify-content-start">
        <Button
          onClick={() => openEditModal(row.original)}
          color="link"
          size="sm"
          className="btn-icon btn-neutral ml-1 icon-info"
          id={`edit${row.original.ID}`}
        >
          <i className="tim-icons icon-pencil tim-md" />
        </Button>
        <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
          Edit
        </UncontrolledTooltip>
        <Button
          onClick={() => handleStatusToggle(row.original)}
          color="link"
          size="sm"
          className="btn-icon btn-neutral ml-1"
          id={`status${row.original.ID}`}
        >
          {row.original.status === "ACTIVE" ? (
            <i className="fa fa-xmark custom-icon btn-icon icon-danger" />
          ) : (
            <i className="fa fa-check custom-icon btn-icon icon-success" />
          )}
        </Button>
        <UncontrolledTooltip delay={0} target={`status${row.original.ID}`}>
          {row.original.status === "ACTIVE" ? "Inactivate" : "Activate"}
        </UncontrolledTooltip>
      </div>
    ),
  },
];

  return (
    <Container fluid className="content mt-4">
      <NotificationAlert ref={notificationAlertRef} />

      <Row>
        <Col md="12">
          <Card className="card-user">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h3" className="font-weight-normal">
                Product Categories
              </CardTitle>
              <Button
                color="default"
                onClick={openAddModal}
                className="btn-round btn-style"
              >
                <i className="fa fa-plus mr-1" /> Product Category
              </Button>
            </CardHeader>

            <CardBody>
              <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                <div className="d-flex flex-wrap align-items-center filter-group">
                  <Input
                    type="text"
                    placeholder="Search by product category"
                    className="mr-2 mb-2 filter-input-large"
                    value={searchState.query}
                    onChange={(e) =>
                      setSearchState((prev) => ({
                        ...prev,
                        query: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                    maxLength={150}
                  />
                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={manufacturerListData?.data?.manufacturer || []}
                    placeholder="Manufacturer"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    value={searchState.manufacturer}
                    getOptionLabel={(options) => options.name}
                    getOptionValue={(options) => options.ID}
                    onChange={(opt) =>
                      setSearchState((prev) => ({
                        ...prev,
                        manufacturer: opt,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={CREATED_BY}
                    placeholder="Created by"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    value={searchState.createdBy}
                    onChange={(opt) =>
                      setSearchState((prev) => ({
                        ...prev,
                        createdBy: opt,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />

                  <Select
                    className="mr-2 mb-2 filter-select"
                    options={MASTER_STATUS_OPTIONS}
                    placeholder="Status"
                    classNamePrefix="react-select"
                    styles={fixedWidthCustomStyle()}
                    components={{ Option, SingleValue }}
                    value={searchState.status}
                    onChange={(opt) =>
                      setSearchState((prev) => ({
                        ...prev,
                        status: opt,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                  />
                  <Button
                    color="default"
                    className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                    onClick={handleSearch}
                  >
                    {productListState.showLoaderOnClick ? (
                      <i className="fa fa-spinner fa-spin search-loader" />
                    ) : (
                      <>
                        <i className="fa fa-search mr-1" />
                        Search
                      </>
                    )}
                  </Button>
                  {searchState.filterParams.length > 0 && (
                    <Button
                      color="default"
                      className="mb-3 filter-btn btn-simple mt-2"
                      onClick={clearSearch}
                    >
                      <i className="fa fa-times mr-1" /> Clear
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 mb-3 mx-1">
                {productCategoriesList?.data?.totalRecords ? (
                  searchState.filterParams &&
                    searchState.filterParams.length > 0 ? (
                    <span className="font-weight-bold">
                      Showing{" "}
                      {productCategoriesList?.data?.totalFilteredRecords} of{" "}
                      {productCategoriesList?.data?.totalRecords} Product
                      Categories
                    </span>
                  ) : (
                    <span className="font-weight-bold">
                      Total Product Category:{" "}
                      {productCategoriesList?.data?.totalRecords}
                    </span>
                  )
                ) : null}
              </div>

              {productListState.isLoading ? (
                <div className="loaderContainer">
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </div>
              ) : productListState.error ? (
                <div className="loaderContainer">{productListState.error}</div>
              ) : productListState.noRecords ? (
                <div className="loaderContainer text-muted">
                  No product category added yet.
                </div>
              ) : (
                <div className="table-responsive product-category-table">
                  <CommonTable
                    data={formattedProductCateList}
                    resizable={false}
                    columns={columns}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    showPaginationBottom={true}
                    className="-striped -highlight"
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      productCategoriesList?.data?.totalFilteredRecords /
                      (productCategoriesList?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <RightPanelCard
        isOpen={mode.modalOpen}
        toggle={closeModal}
        className="modal-lg"
      >
        <Card className="mb-0">
          <CardHeader
            className="d-flex align-items-center justify-content-between mb-2"
            tag="h4"
          >
            {mode.mode === "EDIT"
              ? "Edit Product Category"
              : "Add Product Category"}
            <i
              className="fa fa-close text-muted pointer"
              onClick={closeModal}
            />
          </CardHeader>

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              mode.mode === "EDIT" ? handleEdit() : handleSave();
            }}
          >
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup
                    className={`has-label ${productCategoryData.categoryName.error ? "has-danger" : ""
                      } d-flex flex-column align-items-start`}
                  >
                    <Label for="categoryName">
                      Category Name <span className="validation-error">*</span>
                    </Label>
                    <Input
                      id="categoryName"
                      name="categoryName"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter category name"
                      value={productCategoryData.categoryName.value}
                      onChange={(e) =>
                        handleInputChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      maxLength={100}
                      className="mb-1"
                    />
                    {productCategoryData.categoryName.error && (
                      <small className="validation-error">
                        {productCategoryData.categoryName.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-2">
                  <FormGroup
                    className={`d-flex flex-column align-items-start ${productCategoryData.manufacturer.error ? "has-danger" : ""
                      }`}
                  >
                    <Label>
                      Manufacturer <span className="validation-error">*</span>
                    </Label>
                    <Select
                      className={`text-left ${productCategoryData.manufacturer.error
                        ? "is-invalid"
                        : ""
                        }`}
                      options={manufacturerListData?.data?.manufacturer}
                      placeholder="Select Manufacturer"
                      classNamePrefix="react-select"
                      styles={customStyle}
                      value={productCategoryData.manufacturer.value || null}
                      getOptionLabel={(options) => options.name}
                      getOptionValue={(options) => options.ID}
                      onChange={(selected) =>
                        handleInputChange("manufacturer", selected)
                      }
                      onBlur={() =>
                        handleOnBlur(
                          "manufacturer",
                          productCategoryData.manufacturer.value,
                        )
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {productCategoryData.manufacturer.error && (
                      <small className="validation-error">
                        {productCategoryData.manufacturer.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>

                {mode.mode === "EDIT" && (
                  <Col md="6" className="mb-2">
                    <FormGroup
                      className={`d-flex flex-column align-items-start ${productCategoryData.createdBy.error ? "has-danger" : ""
                        }`}
                    >
                      <Label>
                        Created By <span className="validation-error">*</span>
                      </Label>

                      <Select
                        options={CREATED_BY}
                        placeholder="Select created by"
                        className={`text-left ${productCategoryData.createdBy.error
                          ? "is-invalid"
                          : ""
                          }`}
                        classNamePrefix="react-select"
                        styles={customStyle}
                        value={CREATED_BY.find(
                          (item) =>
                            item.value === productCategoryData.createdBy.value,
                        )}
                        onChange={(selectedOption) =>
                          handleInputChange("createdBy", selectedOption.value)
                        }
                        onBlur={() =>
                          handleOnBlur(
                            "createdBy",
                            productCategoryData.createdBy.value,
                          )
                        }
                      />

                      {productCategoryData.createdBy.error && (
                        <small className="validation-error">
                          {productCategoryData.createdBy.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                )}
                <Col md="6" className="mb-3">
                  <FormGroup>
                    <Label>
                      Status <span className="validation-error">*</span>
                    </Label>
                    <div className="radio-rapper">
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="ACTIVE"
                            checked={
                              productCategoryData.status.value === "ACTIVE"
                            }
                            onChange={(e) =>
                              handleInputChange("status", e.target.value)
                            }
                          />{" "}
                          Active
                        </Label>
                      </FormGroup>
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="INACTIVE"
                            checked={
                              productCategoryData.status.value === "INACTIVE"
                            }
                            onChange={(e) =>
                              handleInputChange("status", e.target.value)
                            }
                          />{" "}
                          Inactive
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter className="d-flex justify-content-end">
              <Button
                color="secondary"
                type="button"
                onClick={closeModal}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                color="default"
                type="submit"
                disabled={Object.values(productCategoryData).some(
                  (item) => item.error !== "",
                )}
              >
                {showLoaderOnClick ? (
                  <i className="fa fa-spinner fa-spin fa-lg text-white" />
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          </Form>
        </Card>
      </RightPanelCard>
    </Container>
  );
};

export default ProductCategory;
